import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, InputGroup, Toast, ToastContainer, Row, Col, Badge, ProgressBar, Modal, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, push, remove } from 'firebase/database';
import { Car } from '../types/car';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Link } from 'react-router-dom';
import useKonamiCode from '../hooks/useKonamiCode';
import KonamiEasterEgg from '../components/KonamiEasterEgg';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface NewFill {
  odometer: string;
  fuelamount: string;
  price: string;
}

interface Count {
  id: string;
  timestamp: number;
}

interface Fill {
  id?: string;
  timestamp: number;
  odometer: number;
  fuelamount: number;
  price: number;
}

interface ConsumptionData {
  date: string;
  consumption: number;
}

interface WorkLocation {
  id: string;
  name: string;
  color: string;
  is_active?: boolean;
}

interface TransportMeans {
  id: string;
  name: string;
  vega: boolean;
}

interface WorkDay {
  date: string;
  locationId: string;
  transportToWorkId?: string;
  transportToHomeId?: string;
  notes?: string;
}

const Home = () => {
  const { currentUser } = useAuth();
  
  // Auto section state
  const [ownedCarIds, setOwnedCarIds] = useState<string[]>([]);
  const [cars, setCars] = useState<Record<string, Car>>({});
  const [selectedCar, setSelectedCar] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [newFill, setNewFill] = useState<NewFill>({
    odometer: '',
    fuelamount: '',
    price: ''
  });
  const [recentFills, setRecentFills] = useState<Fill[]>([]);
  const [avgConsumption, setAvgConsumption] = useState<number>(0);
  const [consumptionHistory, setConsumptionHistory] = useState<ConsumptionData[]>([]);
  const [showAddFillModal, setShowAddFillModal] = useState(false);
  const [lastConsumption, setLastConsumption] = useState<number | null>(null);
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'danger', message: string} | null>(null);
  const [showCarSelectModal, setShowCarSelectModal] = useState(false);
  
  // Easter egg state
  const [showEasterEgg, setShowEasterEgg] = useState(false);

  // Velo section state
  const [counts, setCounts] = useState<Count[]>([]);
  const [totalCounts, setTotalCounts] = useState(0);
  const [rolloverDate, setRolloverDate] = useState('');
  const [countsSinceRollover, setCountsSinceRollover] = useState(0);
  const [veloGaPrice, setVeloGaPrice] = useState('');
  const [singleTicketPrice, setSingleTicketPrice] = useState('7.2'); // Default value
  const [recentRides, setRecentRides] = useState<Count[]>([]);
  const [burndownTarget, setBurndownTarget] = useState<number>(0);
  const [daysRemaining, setDaysRemaining] = useState<number>(0);
  const [daysElapsed, setDaysElapsed] = useState<number>(0);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);

  // Work location state
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [transportMeans, setTransportMeans] = useState<TransportMeans[]>([]);
  const [recentWorkDays, setRecentWorkDays] = useState<WorkDay[]>([]);
  const [workDays, setWorkDays] = useState<Record<string, WorkDay>>({});
  const [showWorkDayModal, setShowWorkDayModal] = useState(false);
  const [selectedWorkDate, setSelectedWorkDate] = useState<Date | null>(null);
  const [selectedWorkLocation, setSelectedWorkLocation] = useState<string>('');
  const [selectedTransportToWork, setSelectedTransportToWork] = useState<string>('');
  const [selectedTransportToHome, setSelectedTransportToHome] = useState<string>('');
  const [workNotes, setWorkNotes] = useState<string>('');

  const activeWorkLocations = workLocations.filter((location) => location.is_active !== false);

  useEffect(() => {
    if (!currentUser) return;

    // Auto section - Fetch owned car IDs
    const ownedCarsRef = ref(database, `user_settings/${currentUser.uid}/ownedCars`);
    const ownedCarsUnsubscribe = onValue(ownedCarsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setOwnedCarIds(Object.values(data));
    });

    // Auto section - Fetch all cars data
    const carsRef = ref(database, 'cars');
    const carsUnsubscribe = onValue(carsRef, (snapshot) => {
      const data = snapshot.val();
      setCars(data || {});
    });

    // Auto section - Fetch selected car
    const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
    const selectedCarUnsubscribe = onValue(selectedCarRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedCar(data || '');
    });

    // Velo section - Fetch VeGa data
    const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
    const dateRef = ref(database, `vega/${currentUser.uid}/rollover_date`);
    const priceRef = ref(database, `vega/${currentUser.uid}/velo_ga_price`);
    const singleTicketPriceRef = ref(database, `vega/${currentUser.uid}/price_single_ticket`);
    const targetRef = ref(database, `vega/${currentUser.uid}/target_rides`);

    const unsubscribeTarget = onValue(targetRef, (snapshot) => {
      const target = snapshot.val();
      if (target) {
        setBurndownTarget(parseInt(target.toString()));
      } else {
        setBurndownTarget(365); // Default target if not set
      }
    });

    const unsubscribeDate = onValue(dateRef, (snapshot) => {
      const date = snapshot.val();
      if (date) {
        setRolloverDate(date);
      }
    });

    const unsubscribePrice = onValue(priceRef, (snapshot) => {
      const price = snapshot.val();
      if (price) {
        setVeloGaPrice(price.toString());
      }
    });

    const unsubscribeSingleTicketPrice = onValue(singleTicketPriceRef, (snapshot) => {
      const price = snapshot.val();
      if (price) {
        setSingleTicketPrice(price.toString());
      } else {
        // If no price is set in the database, set default value
        set(singleTicketPriceRef, 7.2);
        setSingleTicketPrice('7.2');
      }
    });

    const unsubscribeCounts = onValue(countsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const countsArray = Object.entries(data).map(([id, timestamp]) => ({
        id,
        timestamp: timestamp as number
      })).sort((a, b) => b.timestamp - a.timestamp);
      
      setCounts(countsArray);
      setTotalCounts(countsArray.length);
      setRecentRides(countsArray.slice(0, 5));
    });

    // Fetch work locations
    const locationsRef = ref(database, `work/${currentUser.uid}/work_locations`);
    const unsubscribeLocations = onValue(locationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const locationsList = Object.entries(data).map(([id, location]: [string, any]) => ({
        id,
        ...location
      }));
      setWorkLocations(locationsList);
    });
    
    // Fetch transport means
    const transportRef = ref(database, `work/${currentUser.uid}/work_meansoftransport`);
    const unsubscribeTransport = onValue(transportRef, (snapshot) => {
      const data = snapshot.val() || {};
      const transportList = Object.entries(data).map(([id, transport]: [string, any]) => ({
        id,
        name: transport.name || '',
        vega: transport.vega || false
      }));
      setTransportMeans(transportList);
    });

    // Fetch recent work days
    const workDaysRef = ref(database, `work/${currentUser.uid}/work_calendar`);
    const unsubscribeWorkDays = onValue(workDaysRef, (snapshot) => {
      const data = snapshot.val() || {};
      const workDaysList = Object.entries(data)
        .map(([date, day]: [string, any]) => ({
          date,
          ...day
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5); // Get only the 5 most recent entries
      
      setRecentWorkDays(workDaysList);
      setWorkDays(data); // Store the complete work days data
    });

    return () => {
      // Auto section cleanup
      ownedCarsUnsubscribe();
      carsUnsubscribe();
      selectedCarUnsubscribe();
      
      // Velo section cleanup
      unsubscribeCounts();
      unsubscribeDate();
      unsubscribePrice();
      unsubscribeSingleTicketPrice();
      unsubscribeTarget();
      
      // Work location cleanup
      unsubscribeLocations();
      unsubscribeWorkDays();
      unsubscribeTransport();
    };
  }, [currentUser]);

  // Update counts since rollover whenever counts or rollover date changes
  useEffect(() => {
    if (rolloverDate && counts.length > 0) {
      const rolloverTimestamp = Math.floor(new Date(rolloverDate).getTime() / 1000);
      const countsSince = counts.filter(count => count.timestamp >= rolloverTimestamp).length;
      setCountsSinceRollover(countsSince);
      
      // Calculate burndown stats
      const startDate = new Date(rolloverDate);
      const today = new Date();
      const endDate = new Date(rolloverDate);
      endDate.setFullYear(startDate.getFullYear() + 1);
      
      const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const elapsedDays = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const remainingDays = totalDays - elapsedDays;
      
      setDaysElapsed(elapsedDays);
      setDaysRemaining(remainingDays);
      
      // Calculate progress percentage using VeGa price / single ticket price as target
      const targetRides = Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice) || burndownTarget);
      const actualProgress = countsSince;
      const progressPercentage = Math.min(100, Math.round((actualProgress / targetRides) * 100));
      setProgressPercentage(progressPercentage);
    } else {
      setCountsSinceRollover(0);
      setDaysElapsed(0);
      setDaysRemaining(0);
      setProgressPercentage(0);
    }
  }, [counts, rolloverDate, burndownTarget, veloGaPrice, singleTicketPrice]);

  // Fetch recent fills and calculate average consumption when selected car changes
  useEffect(() => {
    if (!currentUser || !selectedCar) {
      setRecentFills([]);
      setAvgConsumption(0);
      setConsumptionHistory([]);
      return;
    }

    const fillsRef = ref(database, `cars/${selectedCar}/fills`);
    const unsubscribe = onValue(fillsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const fillsList = Object.values(data) as Fill[];
      
      // Sort by timestamp for recent fills (descending)
      const timestampSortedFills = [...fillsList].sort((a, b) => b.timestamp - a.timestamp);
      setRecentFills(timestampSortedFills.slice(0, 3));
      
      // Calculate average consumption - EXACTLY as in Moscht.tsx
      if (fillsList.length >= 2) {
        // Sort by timestamp for consumption calculation (ascending)
        const sortedFills = [...fillsList].sort((a, b) => a.timestamp - b.timestamp);
        let totalConsumption = 0;
        let validPairs = 0;
        
        // Create consumption history data
        const history: ConsumptionData[] = [];
        
        for (let i = 1; i < sortedFills.length; i++) {
          const distance = parseFloat((sortedFills[i].odometer - sortedFills[i-1].odometer).toString());
          if (distance > 0) {
            const fuelAmount = parseFloat(sortedFills[i].fuelamount.toString());
            const consumption = (fuelAmount / distance) * 100; // L/100km
            totalConsumption += consumption;
            validPairs++;
            
            // Add to history with formatted date
            const date = new Date(sortedFills[i].timestamp);
            const formattedDate = date.toLocaleDateString('de-CH', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
            
            history.push({
              date: formattedDate,
              consumption: parseFloat(consumption.toFixed(1))
            });
          }
        }
        
        setAvgConsumption(validPairs > 0 ? totalConsumption / validPairs : 0);
        setConsumptionHistory(history);
      } else {
        setAvgConsumption(0);
        setConsumptionHistory([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser, selectedCar]);

  // Auto section - Car selection handler
  const handleCarSelect = async (carId: string) => {
    if (!currentUser) return;
    
    try {
      const selectedCarRef = ref(database, `user_settings/${currentUser.uid}/selectedCar`);
      await set(selectedCarRef, carId);
      setSelectedCar(carId);
    } catch (error) {
      console.error('Error setting selected car:', error);
    }
  };

  // Auto section - Submit form handler
  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault();
    if (!currentUser || !selectedCar) return;

    try {
      const currentOdometer = Number(newFill.odometer);
      const currentFuelAmount = Number(newFill.fuelamount);

      // Create fill entry in database
      const fillsRef = ref(database, `cars/${selectedCar}/fills`);
      await push(fillsRef, {
        timestamp: Date.now(),
        odometer: currentOdometer,
        fuelamount: currentFuelAmount,
        price: Number(newFill.price)
      });

      // Calculate consumption
      const consumption = calculateFuelConsumption(currentOdometer, currentFuelAmount);
      setLastConsumption(consumption);

      // Reset form and close modal
      resetForm();
      setShowAddFillModal(false);

      // Show success toast
      setShowToast(true);
    } catch (error) {
      console.error('Error creating fill:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Speichern der Tankfüllung. Bitte versuchen Sie es erneut.'
      });
    }
  };

  const resetForm = () => {
    setNewFill({
      odometer: '',
      fuelamount: '',
      price: ''
    });
  };

  const calculateFuelConsumption = (currentOdometer: number, currentFuelAmount: number) => {
    // Sort fills by odometer in descending order
    const sortedFills = [...recentFills].sort((a, b) => b.odometer - a.odometer);
    
    // Find the previous fill (next highest odometer reading)
    const previousFill = sortedFills.find(fill => fill.odometer < currentOdometer);
    
    if (previousFill) {
      const distance = currentOdometer - previousFill.odometer;
      const consumption = (currentFuelAmount / distance) * 100; // L/100km
      return consumption;
    }
    
    return null;
  };

  // Velo section - Increment counter
  const incrementCounter = async (date?: Date) => {
    if (!currentUser) return;
    
    try {
      // Use provided date or current date
      const dateToUse = date ? new Date(date) : new Date();
      // If a date was provided, set its time to the current time-of-day
      if (date) {
        const now = new Date();
        dateToUse.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      }
      const timestamp = Math.floor(dateToUse.getTime() / 1000);
      
      const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
      await push(countsRef, timestamp);
    } catch (error) {
      console.error('Error updating counter:', error);
    }
  };

  // Filter owned cars to only show active ones
  const activeCars = ownedCarIds
    .filter(id => cars[id] && cars[id].is_active)
    .map(id => ({
      id,
      ...cars[id]
    }));

  // Format date for display
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate savings from VeGa
  const calculateSavings = () => {
    if (!veloGaPrice || !singleTicketPrice || !rolloverDate || countsSinceRollover === 0) return 0;
    
    const veloGaPriceValue = parseFloat(veloGaPrice);
    const singleTicketPriceValue = parseFloat(singleTicketPrice);
    return Math.round((countsSinceRollover * singleTicketPriceValue) - veloGaPriceValue);
  };

  // Get burndown status text and color
  const getBurndownStatus = () => {
    if (!rolloverDate || daysElapsed === 0 || !veloGaPrice || !singleTicketPrice) return { text: 'Keine Daten', color: 'secondary' };
    
    const targetRides = Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice) || burndownTarget);
    const actualProgress = countsSinceRollover;
    
    if (actualProgress >= targetRides * 1.1) {
      return { text: 'Sehr gut', color: 'success' };
    } else if (actualProgress >= targetRides) {
      return { text: 'Im Plan', color: 'primary' };
    } else if (actualProgress >= targetRides * 0.9) {
      return { text: 'Knapp', color: 'warning' };
    } else {
      return { text: 'Im Rückstand', color: 'danger' };
    }
  };

  // Prepare chart data for consumption history
  const chartData = {
    labels: consumptionHistory.map(item => item.date),
    datasets: [
      {
        label: 'Verbrauch (l/100km)',
        data: consumptionHistory.map(item => item.consumption),
        borderColor: '#1780a6',
        backgroundColor: 'rgba(23, 128, 166, 0.2)',
        borderWidth: 2,
        pointBackgroundColor: '#1780a6',
        pointRadius: 4,
        tension: 0.3,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'l/100km'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Datum'
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${context.raw} l/100km`;
          }
        }
      }
    },
  };

  // Handle work day click
  const handleWorkDayClick = (date: Date) => {
    setSelectedWorkDate(date);
    
    // Format date as YYYY-MM-DD using local timezone
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    // Check if there's an existing entry for this date
    const existingWorkDay = workDays && Object.entries(workDays)
      .find(([key]) => key === dateString);
    
    if (existingWorkDay) {
      const [_, workDay] = existingWorkDay as [string, WorkDay];
      setSelectedWorkLocation(workDay.locationId || '');
      setSelectedTransportToWork(workDay.transportToWorkId || '');
      setSelectedTransportToHome(workDay.transportToHomeId || '');
      setWorkNotes(workDay.notes || '');
    } else {
      // Reset form for new entry
      setSelectedWorkLocation('');
      setSelectedTransportToWork('');
      setSelectedTransportToHome('');
      setWorkNotes('');
    }
    
    setShowWorkDayModal(true);
  };

  // Save work day
  const saveWorkDay = async () => {
    if (!currentUser || !selectedWorkDate || !selectedWorkLocation) return;
    
    // Format date as YYYY-MM-DD using local timezone
    const localDate = new Date(selectedWorkDate);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const workDayRef = ref(database, `work/${currentUser.uid}/work_calendar/${dateString}`);
    
    try {
      // Save the work day entry
      await set(workDayRef, {
        date: dateString,
        locationId: selectedWorkLocation,
        transportToWorkId: selectedTransportToWork || null,
        transportToHomeId: selectedTransportToHome || null,
        notes: workNotes.trim() || null
      });

      // Check if any of the selected transports has VeGa property set to true
      let vegaRidesCount = 0;
      
      // Check transport to work
      if (selectedTransportToWork) {
        const transportToWork = transportMeans.find(t => t.id === selectedTransportToWork);
        if (transportToWork && transportToWork.vega) {
          vegaRidesCount++;
        }
      }
      
      // Check transport to home
      if (selectedTransportToHome) {
        const transportToHome = transportMeans.find(t => t.id === selectedTransportToHome);
        if (transportToHome && transportToHome.vega) {
          vegaRidesCount++;
        }
      }
      
      // Add VeGa rides if any transport with VeGa property was selected
      if (vegaRidesCount > 0) {
        // Add VeGa rides for each transport with VeGa property
        for (let i = 0; i < vegaRidesCount; i++) {
          await incrementCounter(selectedWorkDate);
        }
        
        // Show success message with VeGa rides info
        setAlertMessage({
          type: 'success',
          message: `Arbeitstag gespeichert und ${vegaRidesCount} VeGa ${vegaRidesCount === 1 ? 'Fahrt' : 'Fahrten'} erfasst`
        });
      } else {
        // Show regular success message
        setAlertMessage({
          type: 'success',
          message: 'Arbeitstag wurde erfolgreich gespeichert'
        });
      }
      
      setShowWorkDayModal(false);
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving work day:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Speichern des Arbeitstages'
      });
      setTimeout(() => {
        setAlertMessage(null);
      }, 3000);
    }
  };
  
  // Delete work day
  const deleteWorkDay = () => {
    if (!currentUser || !selectedWorkDate) return;
    
    // Format date as YYYY-MM-DD using local timezone
    const localDate = new Date(selectedWorkDate);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const workDayRef = ref(database, `work/${currentUser.uid}/work_calendar/${dateString}`);
    
    remove(workDayRef)
      .then(() => {
        setShowWorkDayModal(false);
        setAlertMessage({
          type: 'success',
          message: 'Arbeitstag wurde erfolgreich gelöscht'
        });
        setTimeout(() => {
          setAlertMessage(null);
        }, 3000);
      })
      .catch(error => {
        console.error('Error deleting work day:', error);
        setAlertMessage({
          type: 'danger',
          message: 'Fehler beim Löschen des Arbeitstages'
        });
        setTimeout(() => {
          setAlertMessage(null);
        }, 3000);
      });
  };

  // Konami code handler
  const handleKonamiCode = useCallback(() => {
    setShowEasterEgg(true);
  }, []);
  
  // Use the Konami code hook
  useKonamiCode(handleKonamiCode);

  if (!currentUser) return null;

  const burndownStatus = getBurndownStatus();

  return (
    <div>
      <ToastContainer 
        className="p-3" 
        position="top-end"
        style={{ zIndex: 1 }}
      >
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={6000} 
          autohide
          bg="success"
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto">Erfolg</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Tankfüllung wurde erfolgreich gespeichert
            {lastConsumption !== null && (
              <div className="mt-1">
                Verbrauch: {lastConsumption.toFixed(1)} l/100km
              </div>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {alertMessage && (
        <Alert 
          variant={alertMessage.type} 
          onClose={() => setAlertMessage(null)} 
          dismissible 
          className="mb-4"
        >
          {alertMessage.message}
        </Alert>
      )}

      <h1 className="mb-4">Dashboard</h1>
      
      {/* Quick Actions Row */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Schnellzugriff</Card.Title>
          <div className="d-flex flex-wrap gap-2">
            <Button 
              variant="outline-primary" 
              onClick={() => incrementCounter()} 
              className="d-flex align-items-center"
            >
              <span className="me-2">🚲</span> Fahrt erfassen
            </Button>
            <Button 
              variant="outline-primary"
              className="d-flex align-items-center"
              href="/dashboard/vega/rides"
            >
              <span className="me-2">🚴</span> Fahrten anzeigen
            </Button>
            <Button 
              variant="outline-primary"
              className="d-flex align-items-center"
              onClick={() => {
                if (!selectedCar) {
                  setAlertMessage({
                    type: 'danger',
                    message: 'Bitte wählen Sie zuerst ein Fahrzeug aus'
                  });
                  setTimeout(() => {
                    setAlertMessage(null);
                  }, 3000);
                  return;
                }
                resetForm();
                setShowAddFillModal(true);
              }}
            >
              <span className="me-2">⛽</span> Tanken
            </Button>
            <Button 
              variant="outline-primary"
              className="d-flex align-items-center"
              href="/dashboard/moscht/tankungen"
            >
              <span className="me-2">⛽</span> Tankungen anzeigen
            </Button>
            <Button 
              variant="outline-primary"
              className="d-flex align-items-center"
              href="/dashboard/moscht/cars"
            >
              <span className="me-2">🚗</span> Fahrzeuge verwalten
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Main Content */}
      <Row className="g-4">
        {/* Left Column - Work Location Card */}
        <Col lg={6}>
          <h2 className="mb-3">
            <span className="me-2">📍</span>
            Arbeitsorte
          </h2>
          
          {/* Arbeitskalender Card */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Arbeitskalender</Card.Title>
              
              {workLocations.length > 0 ? (
                <div>
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Aktuelle Woche</h6>
                    <div className="d-flex justify-content-between mb-3">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const date = new Date();
                        date.setDate(date.getDate() - date.getDay() + 1 + index); // Monday to Friday
                        
                        // Format date as YYYY-MM-DD using local timezone
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const dateString = `${year}-${month}-${day}`;
                        
                        const workDay = recentWorkDays.find(day => day.date === dateString);
                        const location = workDay ? workLocations.find(loc => loc.id === workDay.locationId) : null;
                        
                        return (
                          <div 
                            key={index} 
                            className="text-center" 
                            style={{ flex: '1', cursor: 'pointer' }}
                            onClick={() => handleWorkDayClick(date)}
                          >
                            <div className="small text-muted">
                              {['Mo', 'Di', 'Mi', 'Do', 'Fr'][index]}
                            </div>
                            <div 
                              style={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                backgroundColor: location ? location.color : '#f8f9fa',
                                border: '1px solid #dee2e6',
                                margin: '5px auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: location ? '#fff' : '#6c757d',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {date.getDate()}
                            </div>
                            {location && (
                              <div className="small text-truncate" style={{ maxWidth: '100%' }}>
                                {location.name}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="w-100"
                      href="/dashboard/work"
                    >
                      Kalender öffnen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Keine Arbeitsorte konfiguriert</p>
                  <Button 
                    variant="outline-primary"
                    size="sm"
                    href="/dashboard/work-settings"
                  >
                    Arbeitsorte konfigurieren
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
          
          <Card>
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Arbeitsorte</Card.Title>
              
              {workLocations.length > 0 ? (
                <div>
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Letzte Einträge</h6>
                    {recentWorkDays.length > 0 ? (
                      recentWorkDays.slice(0, 3).map(workDay => {
                        const location = workLocations.find(loc => loc.id === workDay.locationId);
                        return (
                          <div key={workDay.date} className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center">
                              <div 
                                style={{
                                  backgroundColor: location?.color || '#ccc',
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  marginRight: '8px'
                                }}
                              />
                              <span>{new Date(workDay.date).toLocaleDateString('de-DE')}</span>
                            </div>
                            <span>{location?.name || 'Unbekannt'}</span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-muted small">Keine Einträge vorhanden</p>
                    )}
                  </div>
                  
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="w-100"
                      href="/dashboard/work-settings"
                    >
                      Arbeitsorte verwalten
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Keine Arbeitsorte konfiguriert</p>
                  <Button 
                    variant="outline-primary"
                    size="sm"
                    href="/dashboard/work-settings"
                  >
                    Arbeitsorte konfigurieren
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Auto Section */}
        <Col lg={6}>
          <h2 className="mb-3">
            <span className="me-2">🚗</span>
            Auto
          </h2>
          
          {/* Car Stats Card */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                <span style={{ color: '#1780a6' }}>Fahrzeug Übersicht</span>
                {selectedCar && cars[selectedCar] && (
                  <Badge 
                    bg="primary" 
                    pill
                    onClick={() => setShowCarSelectModal(true)}
                  >
                    {cars[selectedCar].name}
                  </Badge>
                )}
              </Card.Title>
              
              {selectedCar && cars[selectedCar] ? (
                <Row className="mt-3">
                  <Col sm={6} className="mb-3">
                    <div className="d-flex flex-column">
                      <span className="text-muted">Durchschnittsverbrauch</span>
                      <h3>{avgConsumption.toFixed(1)} l/100km</h3>
                    </div>
                  </Col>
                  <Col sm={6} className="mb-3">
                    <div className="d-flex flex-column">
                      <span className="text-muted">Letzte Tankung</span>
                      <h3>
                        {recentFills.length > 0 
                          ? formatDate(recentFills[0].timestamp) 
                          : '-'}
                      </h3>
                    </div>
                  </Col>
                  <Col xs={12}>
                    <Button 
                      variant="primary" 
                      size="sm"
                      className="w-100 mb-2"
                      onClick={() => {
                        resetForm();
                        setShowAddFillModal(true);
                      }}
                    >
                      <span className="me-2">⛽</span> Tanken
                    </Button>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="w-100 mb-2"
                      href="/dashboard/moscht"
                    >
                      Details anzeigen
                    </Button>
                  </Col>
                </Row>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted">Bitte wählen Sie ein Fahrzeug aus</p>
                  <Form.Select 
                    value={selectedCar} 
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleCarSelect(e.target.value)}
                    className="mb-3"
                  >
                    <option value="">Fahrzeug auswählen...</option>
                    {activeCars.map(car => (
                      <option key={car.id} value={car.id}>
                        {car.name}
                      </option>
                    ))}
                  </Form.Select>
                  {!activeCars.length && (
                    <Button 
                      variant="primary"
                      href="/dashboard/moscht/cars"
                    >
                      Fahrzeug hinzufügen
                    </Button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
          
          {/* Consumption Chart */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Verbrauchsverlauf</Card.Title>
              
              {selectedCar && consumptionHistory.length > 0 ? (
                <div style={{ height: '300px', marginTop: '20px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">
                    {!selectedCar 
                      ? 'Bitte wählen Sie zuerst ein Fahrzeug aus' 
                      : 'Keine Verbrauchsdaten verfügbar'}
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - VeGa Section */}
        <Col lg={6}>
          <h2 className="mb-3">
            <span className="me-2">🚲</span>
            VeGa
          </h2>
          
          {/* VeGa Stats Card */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                <span style={{ color: '#1780a6' }}>VeGa Statistik</span>
                <Badge bg="primary" pill>Aktuell</Badge>
              </Card.Title>
              <Row className="mt-3">
                <Col sm={6} className="mb-3">
                  <div className="d-flex flex-column">
                    <span className="text-muted">Fahrten seit {rolloverDate}</span>
                    <h3>{countsSinceRollover}</h3>
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="d-flex flex-column">
                    <span className="text-muted">Gesamt Fahrten</span>
                    <h3>{totalCounts}</h3>
                  </div>
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="d-flex flex-column">
                    <span className="text-muted">Ersparnis (ca.)</span>
                    <h3 style={{ color: calculateSavings() >= 0 ? '#198754' : '#dc3545' }}>
                      CHF {calculateSavings()}
                    </h3>
                  </div>
                </Col>
                <Col sm={6}>
                  <Button 
                    onClick={() => incrementCounter()} 
                    variant="primary" 
                    className="w-100"
                  >
                    Fahrt erfassen
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Burndown Card */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-center">
                <span style={{ color: '#1780a6' }}>Burndown</span>
                <Badge bg={burndownStatus.color} pill>{burndownStatus.text}</Badge>
              </Card.Title>
              {rolloverDate ? (
                <div className="mt-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Fortschritt: {progressPercentage}%</span>
                    <span>{countsSinceRollover} / {Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice))} Fahrten</span>
                  </div>
                  <ProgressBar 
                    now={progressPercentage} 
                    variant={burndownStatus.color}
                    className="mb-3"
                  />
                  
                  <div className="d-flex justify-content-between text-muted small mb-3">
                    <span>Start: {rolloverDate}</span>
                    <span>Verbleibend: {daysRemaining} Tage</span>
                  </div>
                  
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Soll</div>
                      <div>{Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice))} Fahrten</div>
                    </div>
                    <div className="text-center">
                      <div className="text-muted small">Differenz</div>
                      <div className={countsSinceRollover >= Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice)) ? 'text-success' : 'text-danger'}>
                        {countsSinceRollover - Math.round(parseFloat(veloGaPrice) / parseFloat(singleTicketPrice))} Fahrten
                      </div>
                    </div>
                    <div className="text-end">
                      <div className="text-muted small">Ist</div>
                      <div>{countsSinceRollover} Fahrten</div>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      className="w-100"
                      href="/dashboard/vega/rides"
                    >
                      Alle Fahrten anzeigen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Keine Daten verfügbar</p>
                  <Button 
                    variant="outline-primary"
                    size="sm"
                    href="/dashboard/vega/settings"
                  >
                    VeGa Einstellungen
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Add Fill Modal */}
      <Modal show={showAddFillModal} onHide={() => setShowAddFillModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Tankfüllung erfassen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCar ? (
            <Form onSubmit={(e: React.FormEvent<HTMLFormElement>) => handleSubmit(e)}>
              <Form.Group className="mb-3">
                <Form.Label>Kilometerstand</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={newFill.odometer}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFill({ ...newFill, odometer: e.target.value })}
                    min="0"
                    required
                  />
                  <InputGroup.Text>km</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Getankte Menge</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={newFill.fuelamount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFill({ ...newFill, fuelamount: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                  />
                  <InputGroup.Text>l</InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Preis</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    value={newFill.price}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewFill({ ...newFill, price: e.target.value })}
                    min="0"
                    step="0.01"
                    required
                  />
                  <InputGroup.Text>CHF</InputGroup.Text>
                </InputGroup>
              </Form.Group>
            </Form>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Bitte wählen Sie zuerst ein Fahrzeug aus</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddFillModal(false)}>
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={() => handleSubmit()}
            disabled={!selectedCar}
          >
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Work Day Modal */}
      <Modal 
        show={showWorkDayModal} 
        onHide={() => setShowWorkDayModal(false)}
        size="lg" // Larger modal for better touch interaction
        centered // Center on screen for better visibility
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Arbeitsort eintragen
            {selectedWorkDate && (
              <div className="text-muted fs-6">
                {selectedWorkDate.toLocaleDateString('de-DE')}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-4"> {/* Increased margin for better spacing */}
              <Form.Label className="fw-bold">Arbeitsort</Form.Label>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {activeWorkLocations.map(location => (
                  <Button 
                    key={location.id}
                    variant={selectedWorkLocation === location.id ? "primary" : "outline-secondary"}
                    className="py-2 px-3" // Larger padding for touch targets
                    style={{
                      borderColor: location.color,
                      borderWidth: "2px",
                      backgroundColor: selectedWorkLocation === location.id ? location.color : "transparent",
                      color: selectedWorkLocation === location.id ? "white" : "inherit",
                      minWidth: "120px", // Ensure buttons are large enough to tap
                      marginBottom: "8px" // Add space between buttons
                    }}
                    onClick={() => {
                      const locationId = location.id;
                      
                      // Check if the selected location is Home Office
                      if (location.name === "Home Office") {
                        // Find or create an N.A. transport option
                        const naTransport = transportMeans.find(t => t.name === "N.A.");
                        const naTransportId = naTransport ? naTransport.id : "";
                        
                        // Set both transport fields to N.A.
                        setSelectedWorkLocation(locationId);
                        setSelectedTransportToWork(naTransportId);
                        setSelectedTransportToHome(naTransportId);
                      } else {
                        // Normal behavior for other locations
                        setSelectedWorkLocation(locationId);
                      }
                    }}
                  >
                    {location.name}
                  </Button>
                ))}
              </div>
              {workLocations.length === 0 && (
                <div className="alert alert-info mt-2">
                  Keine Arbeitsorte konfiguriert. 
                  <Link to="/dashboard/work-settings" className="ms-1 fw-bold">Arbeitsorte hinzufügen</Link>
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Transportmittel zur Arbeit</Form.Label>
              {transportMeans.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {transportMeans.map(transport => (
                    <Button 
                      key={transport.id}
                      variant={selectedTransportToWork === transport.id ? "primary" : "outline-secondary"}
                      className="py-2 px-3 position-relative"
                      style={{
                        minWidth: "120px",
                        marginBottom: "8px",
                        opacity: workLocations.find(loc => loc.id === selectedWorkLocation)?.name === "Home Office" ? 0.5 : 1
                      }}
                      onClick={() => {
                        if (workLocations.find(loc => loc.id === selectedWorkLocation)?.name !== "Home Office") {
                          setSelectedTransportToWork(transport.id);
                        }
                      }}
                      disabled={workLocations.find(loc => loc.id === selectedWorkLocation)?.name === "Home Office"}
                    >
                      {transport.name}
                      {transport.vega && (
                        <span 
                          className="position-absolute" 
                          style={{ 
                            top: "0", 
                            right: "0", 
                            width: "10px", 
                            height: "10px", 
                            backgroundColor: "#28a745", 
                            borderRadius: "50%",
                            transform: "translate(30%, -30%)"
                          }}
                        />
                      )}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info mt-2">
                  Keine Transportmittel konfiguriert. 
                  <Link to="/dashboard/work-settings" className="ms-1 fw-bold">Transportmittel hinzufügen</Link>
                </div>
              )}
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label className="fw-bold">Transportmittel nach Hause</Form.Label>
              {transportMeans.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {transportMeans.map(transport => (
                    <Button 
                      key={transport.id}
                      variant={selectedTransportToHome === transport.id ? "primary" : "outline-secondary"}
                      className="py-2 px-3 position-relative"
                      style={{
                        minWidth: "120px",
                        marginBottom: "8px",
                        opacity: workLocations.find(loc => loc.id === selectedWorkLocation)?.name === "Home Office" ? 0.5 : 1
                      }}
                      onClick={() => {
                        if (workLocations.find(loc => loc.id === selectedWorkLocation)?.name !== "Home Office") {
                          setSelectedTransportToHome(transport.id);
                        }
                      }}
                      disabled={workLocations.find(loc => loc.id === selectedWorkLocation)?.name === "Home Office"}
                    >
                      {transport.name}
                      {transport.vega && (
                        <span 
                          className="position-absolute" 
                          style={{ 
                            top: "0", 
                            right: "0", 
                            width: "10px", 
                            height: "10px", 
                            backgroundColor: "#28a745", 
                            borderRadius: "50%",
                            transform: "translate(30%, -30%)"
                          }}
                        />
                      )}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="alert alert-info mt-2">
                  Keine Transportmittel konfiguriert. 
                  <Link to="/dashboard/work-settings" className="ms-1 fw-bold">Transportmittel hinzufügen</Link>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          {selectedWorkDate && (() => {
            // Check if there's an existing entry for this date
            const localDate = new Date(selectedWorkDate);
            const year = localDate.getFullYear();
            const month = String(localDate.getMonth() + 1).padStart(2, '0');
            const day = String(localDate.getDate()).padStart(2, '0');
            const dateString = `${year}-${month}-${day}`;
            
            return workDays && Object.keys(workDays).includes(dateString) ? (
              <Button 
                variant="danger" 
                onClick={deleteWorkDay}
                size="lg" // Larger button for touch
                className="px-4" // Wider button
              >
                <span className="me-2">🗑️</span> Löschen
              </Button>
            ) : (
              <div></div> // Empty div to maintain layout when no delete button
            );
          })()}
          <div>
            <Button 
              variant="secondary" 
              onClick={() => setShowWorkDayModal(false)} 
              className="me-2"
              size="lg" // Larger button for touch
            >
              Abbrechen
            </Button>
            <Button 
              variant="primary" 
              onClick={saveWorkDay}
              disabled={!selectedWorkLocation}
              size="lg" // Larger button for touch
              className="px-4" // Wider button
            >
              Speichern
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      
      {/* Car Selection Modal */}
      <Modal show={showCarSelectModal} onHide={() => setShowCarSelectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Fahrzeug auswählen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeCars.length > 0 ? (
            <div className="d-grid gap-2">
              {activeCars.map(car => (
                <Button
                  key={car.id}
                  variant={selectedCar === car.id ? "primary" : "outline-primary"}
                  className="text-start"
                  onClick={() => {
                    handleCarSelect(car.id);
                    setShowCarSelectModal(false);
                  }}
                >
                  <div className="d-flex align-items-center">
                    <span className="me-2">🚗</span>
                    <div>
                      <div>{car.name}</div>
                      <small className="text-muted">{car.brand} {car.model}</small>
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">Keine aktiven Fahrzeuge vorhanden</p>
              <Button 
                variant="primary"
                href="/dashboard/moscht/cars"
              >
                Fahrzeug hinzufügen
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCarSelectModal(false)}>
            Schließen
          </Button>
          <Button 
            variant="outline-primary" 
            href="/dashboard/moscht/cars"
          >
            Fahrzeuge verwalten
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Konami Code Easter Egg */}
      <KonamiEasterEgg 
        show={showEasterEgg} 
        onHide={() => setShowEasterEgg(false)} 
      />
    </div>
  );
};

export default Home;
